import styled from '@emotion/styled';
import { Grid, Typography } from '@material-ui/core';
import { createElement, FC, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import { useGetIsPC } from '../../../../utilityHooks/useGetIsPC';
import { EnhancedSearchFileUploader as _EnhancedSearchFileUploader } from '../../../connected/EnhancedSearchFileUploader';
import { SPACING_REGULAR } from '../../../constant/spacing';
import mq from '../../../styleUtility/mediaquery';
import { FileIdsValue } from '../../../types/DynamicSchema';
import { FileReference, UploadingAssetFilesProgress, UploadingFile } from '../../../types/Files';
import { Meta } from '../../../types/Meta';
import FileListItem from '../CreativeBriefEditor/Field/FileListItem';
import _FileList, { FileListUploadingItem } from '../FileList';
import _FileUploader from '../FileUploader';

type Props = {
  className?: string;
  meta: Meta;
  fieldKey: string;
  bucketId: string;
  fileIds: FileIdsValue;
  projectFiles: ReadonlyArray<FileReference>;
  filesProgress: UploadingAssetFilesProgress['categoryKey'];
  disabled: boolean;
  onRequestDeleteFile: (file: FileReference) => void;
  onRequestUploadFiles: (files: ReadonlyArray<UploadingFile>, bucketId: string, fieldKey: string) => void;
};

const ProjectFileList: FC<Props> = ({
  className,
  meta,
  fieldKey,
  bucketId,
  fileIds,
  projectFiles,
  filesProgress,
  disabled,
  onRequestDeleteFile,
  onRequestUploadFiles,
}) => {
  const files = fileIds != null ? projectFiles.filter((file) => fileIds.includes(file.id)) : [];
  const isPC = useGetIsPC();

  return (
    <Root className={className}>
      {files.length > 0 && (
        <>
          <UploadedFileListHeader disabled={disabled}>
            <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap">
              <Grid item style={{ whiteSpace: 'nowrap' }}>
                <FormattedMessage id="file_list.text" />
              </Grid>
              <Grid item style={{ borderTop: '1px solid #BBBBBB', width: '100%', marginLeft: SPACING_REGULAR }} />
            </Grid>
          </UploadedFileListHeader>
          <FileList disabled={disabled}>
            {files.map((file) => (
              <FileListItem
                isPC={isPC}
                file={file}
                onClickDeleteButton={() => onRequestDeleteFile(file)}
                key={file.id}
              />
            ))}
          </FileList>
        </>
      )}

      {Object.entries(filesProgress).length > 0 && (
        <FileList disabled={disabled}>
          {Object.entries(filesProgress).map(([id, { file, progress, state }]) => (
            <FileListUploadingItem file={file} key={`uploading-${id}`} progress={progress} state={state} />
          ))}
        </FileList>
      )}

      {renderUploader({ bucketId, meta, onRequestUploadFiles, fieldKey, disabled })}
    </Root>
  );
};

const renderUploader = ({
  meta,
  bucketId,
  onRequestUploadFiles,
  fieldKey,
  disabled,
}: {
  bucketId: string;
  meta: Meta;
  onRequestUploadFiles: (files: ReadonlyArray<UploadingFile>, bucketId: string, fieldKey: string) => void;
  fieldKey: string;
  disabled: boolean;
}) => {
  const enableSearchUploader = (() => {
    if (meta == null) {
      return false;
    } else if (meta.allowRecommendTypes == null && meta.allowSearchTypes == null) {
      return false;
    } else if (
      (Array.isArray(meta.allowRecommendTypes) && meta.allowRecommendTypes.length !== 0) ||
      (Array.isArray(meta.allowSearchTypes) && meta.allowSearchTypes.length !== 0)
    ) {
      return true;
    }
    return false;
  })();

  const width = enableSearchUploader ? 6 : 12;
  const uploader = {
    fileUploader: (
      <Grid item xs={12} sm={12} md={width} lg={width}>
        <FileUploader
          disabled={disabled}
          iconWidth={enableSearchUploader ? 50 : 25}
          onSelectFiles={(_, files) => onRequestUploadFiles(files, bucketId, fieldKey)}
        />
      </Grid>
    ),
    searchUploader:
      width === 6 ? (
        <Grid item xs={12} sm={12} md={width} lg={width}>
          <EnhancedSearchFileUploader meta={meta} fieldKey={fieldKey} disabled={disabled} />
        </Grid>
      ) : null,
  };

  return (
    <Grid container spacing={2}>
      {uploader.fileUploader}
      {uploader.searchUploader}
    </Grid>
  );
};

const Root = styled('div')`
  padding-bottom: ${SPACING_REGULAR}px;
`;

const FileList = styled(_FileList)<{ disabled: boolean }>`
  padding-bottom: ${SPACING_REGULAR}px;
  .MuiTypography-root {
    ${({ disabled }) => disabled && 'color: #A5A5A5;'};
  }
  .MuiSvgIcon-root {
    ${({ disabled }) => disabled && 'color: #A5A5A5;'};
  }
  .MuiAvatar-colorDefault {
    ${({ disabled }) => disabled && 'color: initial;'};
    ${({ disabled }) => disabled && 'background-color: initial;'};
  }
  ${mq('sp')} {
    padding-top: 0px;
  }
`;

const UploadedFileListHeader = styled(Typography)<{ disabled: boolean }>`
  font-size: 1rem;
  padding-top: ${SPACING_REGULAR}px;
  ${({ disabled }) => disabled && 'color: #A5A5A5;'};
`;

const FileUploader = styled(_FileUploader)<{ disabled: boolean }>`
  .MuiTypography-root {
    ${({ disabled }) => disabled && 'color: #A5A5A5;'};
  }
`;

const EnhancedSearchFileUploader = styled(_EnhancedSearchFileUploader)<{ disabled: boolean }>`
  .MuiTypography-root {
    ${({ disabled }) => disabled && 'color: #A5A5A5;'};
  }
`;

export default ProjectFileList;
